
import { Component, Prop, Vue } from "vue-property-decorator";

import VClamp from "vue-clamp";

const DOC_TITLE = "doc";
const ADD_TITLE = "add";
const OUTDATED_TITLE = "outdated";

const DISABLED_STATE = "disabled";
const ERROR_STATE = "error";
const LOADING_STATE = "loading";

const DATETIME_FORMAT = "en-US";

const ARCHIVE_EVENT = "archive";
const DELETE_EVENT = "delete";

const SAVED_FILE_STATUS = "saved";

const MESSAGES = {
  labels: {
    download: "Download",
    archive: "Archive",
    delete: "Delete",
    addNew: "Add new",
    outdated: "Outdated",
  },
};

const ICONS = {
  disabled: "lock-closed",
  more: "ellipsis-vertical",
  remove: "close-outline",
};

const IMAGE_PROPS = {
  height: 32,
};

const MODEL_CLASS = "nb-new-uploaded-file";

@Component({
  components: {
    VClamp,
  },
})
export default class NbNewUploadedFile extends Vue {
  @Prop({ type: String }) readonly title?: string;
  @Prop({ type: String }) readonly message?: string;
  @Prop({}) readonly file?;
  @Prop({ type: String, default: null }) readonly status?: string;
  @Prop({ type: Array, default: () => [] }) readonly errors?: string[];
  @Prop({ type: String }) readonly datetime?: string;
  @Prop({ type: Boolean, default: false }) readonly loading?: boolean;
  @Prop({ type: Boolean }) readonly disabled?: boolean;
  @Prop({ type: Boolean }) readonly outdated?: boolean;
  @Prop({ type: Boolean, default: true }) readonly canUploadFile?: boolean;
  @Prop({ type: Boolean, default: true }) readonly canArchiveFile?: boolean;
  @Prop({ type: Boolean, default: true }) readonly canDeleteFile?: boolean;
  @Prop({ type: Boolean, default: false }) readonly horizontal?: boolean;
  @Prop({ type: Boolean, default: false }) readonly controls?: boolean;
  @Prop({ type: Boolean, default: false }) readonly selected?: boolean;

  MESSAGES = MESSAGES;
  ICONS = ICONS;

  get classes() {
    const { hasTitle, horizontal, hasErrors, selected, loading } = this;

    return {
      [`${MODEL_CLASS}_cursor-pointer`]: !hasTitle,
      [`${MODEL_CLASS}_horizontal`]: !!horizontal,
      [`${MODEL_CLASS}_error`]: !!(hasErrors && !loading),
      [`${MODEL_CLASS}_selected`]: !!selected,
      [`${MODEL_CLASS}_loading`]: !!loading,
    };
  }

  get fileTitle() {
    return this.hasTitle ? this.title || this.file?.name : MESSAGES.labels.addNew;
  }

  get hasTitle() {
    return !!this.title || this.file?.name;
  }

  get fileMessage() {
    return this.message || this.file?.errors?.[0]?.message;
  }

  get fileIsSaved() {
    return this.status === SAVED_FILE_STATUS;
  }

  get hasAnyAbility() {
    return this.canUploadFile || this.canArchiveFile || this.canDeleteFile;
  }

  get hasDownloadListener() {
    return !!this.$listeners.download;
  }

  get hasArchiveListener() {
    return !!this.$listeners.archive;
  }

  get hasDeleteListener() {
    return !!this.$listeners.delete;
  }

  get hasAnyListener() {
    return this.hasDownloadListener || this.hasArchiveListener || this.hasDeleteListener;
  }

  get isActivatorAvailable() {
    return this.hasTitle && this.hasAnyListener && this.hasAnyAbility && !this.horizontal;
  }

  get hasErrors() {
    return this.errors?.length;
  }

  get image() {
    const title = this.hasTitle ? (this.outdated ? OUTDATED_TITLE : DOC_TITLE) : ADD_TITLE;

    let name = this.disabled ? DISABLED_STATE : title;

    if (this.hasErrors) name = ERROR_STATE;

    if (this.loading) name = LOADING_STATE;

    return {
      src: require(`@/assets/images/upload-${name}.svg`),
      alt: name,
    };
  }

  get imageProps() {
    return { ...this.image, ...IMAGE_PROPS };
  }

  get convertedDatetime() {
    if (!this.datetime || typeof this.datetime !== "string") return "";

    return new Date(this.datetime).toLocaleDateString(DATETIME_FORMAT);
  }

  emitClick() {
    if (this.disabled) return;

    this.$emit("click");
  }

  emitUploaderEvent(emit: string, e: Event) {
    if (!this.canUploadFile) return;

    if (emit === ARCHIVE_EVENT && !this.canArchiveFile) return;

    if (emit === DELETE_EVENT && !this.canDeleteFile) return;

    if (e) e.stopPropagation();

    this.$emit(emit);
  }

  isMenuOpen = false;

  toggleMenu(e: Event) {
    if (e) e.stopPropagation();

    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu() {
    this.isMenuOpen = false;
  }

  deleteFile() {
    this.$emit("delete");
  }
}
